'use client';

import useRepositoryStore from '@/state/repositoryState';
import useUserStore from '@/state/userState';
import { signIn, useSession } from 'next-auth/react';
import { useCallback, useEffect, useState } from 'react';
import { mountScope } from '../utils';

export const AuthProvider = () => {
  const { userRepositoryList, organizationRepositoryList } =
    useRepositoryStore();
  const { isWithCorrectPermissions, setIsWithCorrectPermissions } =
    useUserStore();
  const { data: session } = useSession();
  const [isPrivateRepository, setIsPrivateRepository] = useState(false);

  const signInSilently = useCallback(async () => {
    setIsWithCorrectPermissions(true);
    const provider = session?.user?.provider;

    if (!provider) {
      return;
    }

    await signIn(provider, {
      redirect: false,
      scope: mountScope(provider),
    });
  }, [session, setIsWithCorrectPermissions]);

  useEffect(() => {
    // These lists are no longer populated by an eager bootstrap fetch: they
    // stay empty until some route loads repos on its own (e.g. /documentation,
    // /home). This effect still fires once that happens, since both lists are
    // dependencies, so detection is lazy now rather than at app bootstrap. The
    // proper fix is a backend scalar for "org has a private repo" instead of
    // scanning a full repo list for one boolean.
    [...(userRepositoryList || []), ...(organizationRepositoryList || [])].map(
      (repository) => {
        if (!repository.IsPublic) {
          setIsPrivateRepository(true);
        }
      }
    );
  }, [userRepositoryList, organizationRepositoryList]);

  useEffect(() => {
    if (isPrivateRepository && !isWithCorrectPermissions && session?.user) {
      // Check if session is valid and not about to expire
      if (session.expires) {
        const expiryDate = new Date(session.expires);
        const currentDate = new Date();
        const timeUntilExpiry = expiryDate.getTime() - currentDate.getTime();
        const oneHourInMs = 60 * 60 * 1000;

        // Only sign in silently if session expires in less than 1 hour
        if (timeUntilExpiry < oneHourInMs) {
          signInSilently();
        } else {
          // Session is still valid, just update the permissions state
          setIsWithCorrectPermissions(true);
        }
      } else {
        signInSilently();
      }
    }
  }, [
    isPrivateRepository,
    isWithCorrectPermissions,
    session,
    setIsWithCorrectPermissions,
    signInSilently,
  ]);

  return <></>;
};
